// src/components/CryptoBackground.js
// Site-wide premium light crypto background:
// soft gradient + dot grid, drifting aurora blobs, faded tech grid,
// rotating ring arcs, and raining coins + crypto signs (bull/bear,
// green/red candles, chart patterns) with random flash/glow.
// Rendered once globally as a fixed layer behind all content.
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { GiBull, GiBearFace } from 'react-icons/gi';
import { TbChartCandleFilled, TbChartLine } from 'react-icons/tb';
import { FaArrowTrendUp, FaArrowTrendDown } from 'react-icons/fa6';

import imgBTC from '../assets/coins/btc.png';
import imgETH from '../assets/coins/eth.png';
import imgSOL from '../assets/coins/sol.png';
import imgBNB from '../assets/coins/bnb.png';
import imgXRP from '../assets/coins/xrp.png';
import imgADA from '../assets/coins/ada.png';

const E = '#29ABE2';
const GREEN = '#16c784';
const RED = '#ea3943';

/* ── animations ── */
const auroraDrift = keyframes`
  0%,100% { transform: translate(0,0)        scale(1);    }
  33%      { transform: translate(28px,-22px) scale(1.12); }
  66%      { transform: translate(-22px,16px) scale(0.94); }
`;
const arcSpin = keyframes`
  from { transform: rotate(0deg);   }
  to   { transform: rotate(360deg); }
`;
const coinFall = keyframes`
  0%   { transform: translateY(-150px) rotate(0deg);   opacity: 0; }
  10%  { opacity: var(--op); }
  88%  { opacity: var(--op); }
  100% { transform: translateY(110vh) rotate(330deg);  opacity: 0; }
`;
const signFall = keyframes`
  0%   { transform: translateY(-150px) rotate(-10deg); opacity: 0; }
  10%  { opacity: var(--op); }
  88%  { opacity: var(--op); }
  100% { transform: translateY(110vh) rotate(12deg);   opacity: 0; }
`;
const flashGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 6px 10px rgba(13,34,53,0.10)) brightness(1); }
  46%       { filter: drop-shadow(0 6px 10px rgba(13,34,53,0.10)) brightness(1); }
  54%       { filter: drop-shadow(0 0 18px var(--gc)) drop-shadow(0 0 7px var(--gc)) brightness(1.55); }
  62%       { filter: drop-shadow(0 6px 10px rgba(13,34,53,0.10)) brightness(1); }
`;

/* ── fixed full-viewport root, behind all content ── */
const BgRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background-color: #ffffff;
  background-image:
    radial-gradient(rgba(13,34,53,0.10) 1px, transparent 1px),
    linear-gradient(180deg, #ffffff 0%, #eef6ff 55%, #ffffff 100%);
  background-size: 28px 28px, cover;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  @media (prefers-reduced-motion: reduce) { animation: none; }

  &.b1 { width: 560px; height: 560px; background: radial-gradient(circle, rgba(41,171,226,0.30), transparent 70%); top: -160px; left: -100px;  animation: ${auroraDrift} 19s ease-in-out infinite; }
  &.b2 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(247,148,29,0.18), transparent 70%); top: 38%; right: -120px;    animation: ${auroraDrift} 23s ease-in-out infinite 2s; }
  &.b3 { width: 440px; height: 440px; background: radial-gradient(circle, rgba(124,108,255,0.16), transparent 70%); bottom: -160px; left: 30%; animation: ${auroraDrift} 27s ease-in-out infinite 1s; }
`;

const GridLines = styled.div`
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(13,34,53,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(13,34,53,0.04) 1px, transparent 1px);
  background-size: 64px 64px;
  -webkit-mask-image: radial-gradient(ellipse 75% 60% at 60% 35%, #000 18%, transparent 80%);
  mask-image: radial-gradient(ellipse 75% 60% at 60% 35%, #000 18%, transparent 80%);
`;

const CornerArc = styled.div`
  position: absolute;
  top: -170px; right: -120px;
  width: 520px; height: 520px; border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 0deg, rgba(41,171,226,0.20) 55deg, transparent 160deg, transparent 360deg);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px));
  animation: ${arcSpin} 42s linear infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 760px) { display: none; }
`;

const CornerArc2 = styled(CornerArc)`
  top: auto; right: auto;
  bottom: -200px; left: -150px;
  width: 580px; height: 580px;
  background: conic-gradient(from 140deg, transparent 0deg, rgba(247,148,29,0.16) 50deg, transparent 150deg, transparent 360deg);
  animation: ${arcSpin} 55s linear infinite reverse;
`;

const RainCoin = styled.img`
  position: absolute;
  top: 0;
  object-fit: contain;
  pointer-events: none;
  will-change: transform, opacity, filter;
  filter: drop-shadow(0 6px 10px rgba(13,34,53,0.10));
  animation: ${coinFall} var(--dur, 16s) linear var(--delay, 0s) infinite;

  &.g {
    animation: ${coinFall} var(--dur, 16s) linear var(--delay, 0s) infinite,
               ${flashGlow} var(--gd, 2.6s) ease-in-out var(--gdelay, 0s) infinite;
  }

  &.rm { @media (max-width: 760px) { display: none; } }
  @media (prefers-reduced-motion: reduce) { animation: none; display: none; }
`;

const RainSign = styled.span`
  position: absolute;
  top: 0;
  display: inline-flex;
  pointer-events: none;
  will-change: transform, opacity, filter;
  animation: ${signFall} var(--dur, 18s) linear var(--delay, 0s) infinite;

  &.g {
    animation: ${signFall} var(--dur, 18s) linear var(--delay, 0s) infinite,
               ${flashGlow} var(--gd, 2.6s) ease-in-out var(--gdelay, 0s) infinite;
  }

  svg { width: 100%; height: 100%; display: block; filter: drop-shadow(0 4px 8px rgba(13,34,53,0.08)); }
  &.rm { @media (max-width: 760px) { display: none; } }
  @media (prefers-reduced-motion: reduce) { animation: none; display: none; }
`;

const GC_BLUE = 'rgba(41,171,226,0.95)';
const GC_GOLD = 'rgba(247,184,47,0.95)';
const GC_WHITE = 'rgba(255,255,255,0.95)';
const GC_GREEN = 'rgba(22,199,132,0.95)';
const GC_RED = 'rgba(234,57,67,0.95)';

const RAIN = [
  { img: imgBTC, left: '3%',  size: 44, dur: 15, delay: -2,  op: 0.18, g: 1, gc: GC_GOLD,  gd: 2.4, gdelay: -0.5 },
  { img: imgETH, left: '9%',  size: 28, dur: 19, delay: -8,  op: 0.12, m: true },
  { img: imgSOL, left: '14%', size: 38, dur: 17, delay: -5,  op: 0.16 },
  { img: imgADA, left: '20%', size: 26, dur: 21, delay: -12, op: 0.11, m: true },
  { img: imgBNB, left: '26%', size: 40, dur: 16, delay: -1,  op: 0.15, g: 1, gc: GC_BLUE,  gd: 3.1, gdelay: -1.6 },
  { img: imgXRP, left: '31%', size: 30, dur: 20, delay: -9,  op: 0.12, m: true },
  { img: imgBTC, left: '37%', size: 34, dur: 18, delay: -14, op: 0.13 },
  { img: imgETH, left: '43%', size: 46, dur: 14, delay: -6,  op: 0.17, m: true, g: 1, gc: GC_WHITE, gd: 2.7, gdelay: -2.2 },
  { img: imgSOL, left: '49%', size: 28, dur: 22, delay: -3,  op: 0.12 },
  { img: imgADA, left: '54%', size: 38, dur: 16, delay: -10, op: 0.15, m: true },
  { img: imgBNB, left: '60%', size: 32, dur: 19, delay: -7,  op: 0.13 },
  { img: imgXRP, left: '65%', size: 26, dur: 23, delay: -15, op: 0.11, m: true },
  { img: imgETH, left: '71%', size: 46, dur: 14, delay: -4,  op: 0.17, g: 1, gc: GC_BLUE,  gd: 3.4, gdelay: -0.8 },
  { img: imgBTC, left: '76%', size: 30, dur: 18, delay: -11, op: 0.13, m: true },
  { img: imgSOL, left: '82%', size: 36, dur: 16, delay: -6,  op: 0.15 },
  { img: imgBNB, left: '88%', size: 28, dur: 22, delay: -13, op: 0.12, m: true },
  { img: imgADA, left: '93%', size: 42, dur: 15, delay: -4,  op: 0.16, g: 1, gc: GC_WHITE, gd: 2.9, gdelay: -1.2 },
  { img: imgXRP, left: '97%', size: 32, dur: 20, delay: -9,  op: 0.13, m: true },
];

const SIGN_ICON = {
  bull: GiBull,
  bear: GiBearFace,
  candleUp: TbChartCandleFilled,
  candleDown: TbChartCandleFilled,
  trendUp: FaArrowTrendUp,
  trendDown: FaArrowTrendDown,
  chart: TbChartLine,
};

const SIGNS = [
  { t: 'bull',       left: '6%',  size: 52, dur: 20, delay: -3,  op: 0.14, color: GREEN, g: 1, gc: GC_GREEN, gd: 2.6, gdelay: -0.4 },
  { t: 'candleUp',   left: '12%', size: 40, dur: 17, delay: -10, op: 0.16, color: GREEN, m: true },
  { t: 'trendDown',  left: '17%', size: 38, dur: 21, delay: -6,  op: 0.13, color: RED },
  { t: 'candleDown', left: '23%', size: 36, dur: 18, delay: -13, op: 0.15, color: RED, m: true, g: 1, gc: GC_RED, gd: 3.2, gdelay: -1.5 },
  { t: 'chart',      left: '28%', size: 42, dur: 19, delay: -8,  op: 0.12, color: E },
  { t: 'bear',       left: '34%', size: 50, dur: 23, delay: -14, op: 0.13, color: RED, m: true, g: 1, gc: GC_RED, gd: 2.8, gdelay: -2.1 },
  { t: 'trendUp',    left: '40%', size: 38, dur: 20, delay: -4,  op: 0.14, color: GREEN, g: 1, gc: GC_GREEN, gd: 3.4, gdelay: -0.9 },
  { t: 'candleUp',   left: '46%', size: 40, dur: 16, delay: -11, op: 0.15, color: GREEN, m: true },
  { t: 'chart',      left: '51%', size: 44, dur: 18, delay: -2,  op: 0.12, color: E, g: 1, gc: GC_BLUE, gd: 3.0, gdelay: -1.8 },
  { t: 'bull',       left: '57%', size: 48, dur: 22, delay: -15, op: 0.13, color: GREEN, m: true },
  { t: 'candleDown', left: '63%', size: 40, dur: 16, delay: -9,  op: 0.15, color: RED, g: 1, gc: GC_RED, gd: 2.5, gdelay: -0.6 },
  { t: 'trendDown',  left: '69%', size: 36, dur: 21, delay: -5,  op: 0.13, color: RED, m: true },
  { t: 'trendUp',    left: '74%', size: 38, dur: 20, delay: -12, op: 0.14, color: GREEN },
  { t: 'candleUp',   left: '80%', size: 36, dur: 19, delay: -7,  op: 0.15, color: GREEN, m: true },
  { t: 'bear',       left: '86%', size: 46, dur: 22, delay: -2,  op: 0.12, color: RED, g: 1, gc: GC_RED, gd: 3.3, gdelay: -1.1 },
  { t: 'chart',      left: '91%', size: 40, dur: 18, delay: -10, op: 0.12, color: E, m: true },
  { t: 'trendUp',    left: '96%', size: 40, dur: 17, delay: -1,  op: 0.14, color: GREEN, g: 1, gc: GC_GREEN, gd: 2.7, gdelay: -2.4 },
];

function CryptoBackground() {
  return (
    <BgRoot aria-hidden="true">
      <CornerArc />
      <CornerArc2 />
      <GridLines />
      <Blob className="b1" />
      <Blob className="b2" />
      <Blob className="b3" />
      {RAIN.map((c, i) => (
        <RainCoin
          key={`c${i}`}
          src={c.img}
          alt=""
          className={`${c.m ? 'rm' : ''}${c.g ? ' g' : ''}`}
          style={{
            left: c.left,
            width: c.size,
            height: c.size,
            '--op': c.op,
            '--dur': `${c.dur}s`,
            '--delay': `${c.delay}s`,
            ...(c.g ? { '--gc': c.gc, '--gd': `${c.gd}s`, '--gdelay': `${c.gdelay}s` } : {}),
          }}
        />
      ))}
      {SIGNS.map((s, i) => {
        const Icon = SIGN_ICON[s.t];
        return (
          <RainSign
            key={`s${i}`}
            className={`${s.m ? 'rm' : ''}${s.g ? ' g' : ''}`}
            style={{
              left: s.left,
              width: s.size,
              height: s.size,
              color: s.color,
              '--op': s.op,
              '--dur': `${s.dur}s`,
              '--delay': `${s.delay}s`,
              ...(s.g ? { '--gc': s.gc, '--gd': `${s.gd}s`, '--gdelay': `${s.gdelay}s` } : {}),
            }}
          >
            <Icon />
          </RainSign>
        );
      })}
    </BgRoot>
  );
}

export default CryptoBackground;
